import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import { getText } from 'i18n/intl';
import { isEqual } from 'lodash';
import type {
  ObjectMapping,
  SyncSettingResponse,
} from '@timeedit/calendar-integration-api-contract';
import type { TType } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import { useForm } from '@timeedit/ui-components/antd/form/Form';
import MS365SyncSettingsDetails from './MS365SyncSettingsDetails';

type Props = {
  objectMapping: ObjectMapping | null;
  syncSetting?: SyncSettingResponse | null;
  secondSyncSetting?: SyncSettingResponse | null;
  objectTypes: TType[];
  onClose: (syncSetting: SyncSettingResponse) => void;
  onDelete: (syncSetting: SyncSettingResponse) => void;
  onDiscard: (syncSetting: SyncSettingResponse) => void;
  onSave: (syncSetting: SyncSettingResponse) => void;
};

const MS365SyncSettingsDrawer = ({
  syncSetting,
  secondSyncSetting,
  objectMapping,
  objectTypes,
  onClose,
  onDelete,
  onDiscard,
  onSave,
}: Props) => {
  const [form] = useForm<SyncSettingResponse>();
  const [secondForm] = useForm<SyncSettingResponse>();

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSecondSaveDisabled, setIsSecondSaveDisabled] = useState(true);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(syncSetting);
    setIsSaveDisabled(true);
  }, [syncSetting]);

  useEffect(() => {
    secondForm.resetFields();
    secondForm.setFieldsValue(secondSyncSetting);
    setIsSecondSaveDisabled(true);
  }, [secondSyncSetting]);

  const handleValuesChanged = useCallback(
    (
      values: SyncSettingResponse,
      setDisabled: Dispatch<SetStateAction<boolean>>,
      syncSetting: SyncSettingResponse | null
    ) => {
      setDisabled(() => isEqual(values, syncSetting));
    },
    []
  );
  const handleFirstValuesChange = useCallback(
    (_: Partial<SyncSettingResponse>, values: SyncSettingResponse) =>
      handleValuesChanged(values, setIsSaveDisabled, syncSetting),
    [handleValuesChanged, syncSetting]
  );

  const handleSecondValuesChange = useCallback(
    (_: Partial<SyncSettingResponse>, values: SyncSettingResponse) =>
      handleValuesChanged(values, setIsSecondSaveDisabled, secondSyncSetting),
    [handleValuesChanged, secondSyncSetting]
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: syncSetting?.name ?? '',
        content: (
          <MS365SyncSettingsDetails
            objectMapping={objectMapping}
            syncSetting={syncSetting}
            form={form}
            onValuesChange={handleFirstValuesChange}
            objectTypes={objectTypes}
          />
        ),
        drawerOpen: Boolean(syncSetting),
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText(
              'integration.calendar.sync_settings.actions.confirm_delete'
            )}
            handleDelete={() => onDelete(form.getFieldsValue())}
            handleDiscard={() => onDiscard(form.getFieldsValue())}
            handleSave={() => onSave(form.getFieldsValue())}
            isSaveDisable={isSaveDisabled}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(form.getFieldsValue()),
      }}
      secondDrawer={{
        title: secondSyncSetting?.name ?? '',
        content: (
          <MS365SyncSettingsDetails
            objectMapping={objectMapping}
            syncSetting={secondSyncSetting}
            form={secondForm}
            onValuesChange={handleSecondValuesChange}
            objectTypes={objectTypes}
          />
        ),
        drawerOpen: Boolean(secondSyncSetting),
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText(
              'integration.calendar.sync_settings.actions.confirm_delete'
            )}
            handleDelete={() => onDelete(secondForm.getFieldsValue())}
            handleDiscard={() => onDiscard(secondForm.getFieldsValue())}
            handleSave={() => onSave(secondForm.getFieldsValue())}
            isSaveDisable={isSecondSaveDisabled}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(secondForm.getFieldsValue()),
      }}
    />
  );
};

export default MS365SyncSettingsDrawer;
